import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const AIInstructionHints = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Instruction hints state - only for Program entity
  const [fieldHints, setFieldHints] = useState({
    learning_outcomes: '',
    career_outcomes: '',
    program_modules: '',
    program_highlights: '',
  });

  const entityType = 'program'; // Only Program is supported for now
  const backendUrl = API_BASE;

  useEffect(() => {
    fetchInstructionHints();
  }, []);

  const fetchInstructionHints = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in first');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/ai/instruction-hints`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch instruction hints' }));
        throw new Error(errorData.detail || `Failed to fetch instruction hints (${response.status})`);
      }

      const result = await response.json();
      
      if (result.success && result.hints) {
        // Update state with fetched hints (default to empty string if not present)
        setFieldHints({
          learning_outcomes: result.hints.learning_outcomes || '',
          career_outcomes: result.hints.career_outcomes || '',
          program_modules: result.hints.program_modules || '',
          program_highlights: result.hints.program_highlights || '',
        });
      }
    } catch (err) {
      console.error('Error fetching instruction hints:', err);
      setError(err.message || 'Failed to fetch instruction hints');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    // Enforce max length of 300 characters
    const truncatedValue = value.length > 300 ? value.substring(0, 300) : value;
    setFieldHints(prev => ({
      ...prev,
      [fieldName]: truncatedValue
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in first');
        return;
      }

      // Remove empty hints before sending
      const hintsToSave = {};
      Object.entries(fieldHints).forEach(([key, value]) => {
        if (value && value.trim()) {
          hintsToSave[key] = value.trim();
        }
      });

      const response = await fetch(`${backendUrl}/api/admin/ai/instruction-hints`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          field_hints: hintsToSave
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to save instruction hints' }));
        throw new Error(errorData.detail || `Failed to save instruction hints (${response.status})`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err) {
      console.error('Error saving instruction hints:', err);
      setError(err.message || 'Failed to save instruction hints');
    } finally {
      setSaving(false);
    }
  };

  const fieldLabels = {
    learning_outcomes: 'Learning Outcomes',
    career_outcomes: 'Career Outcomes',
    program_modules: 'Program Modules',
    program_highlights: 'Program Highlights',
  };

  const fieldDescriptions = {
    learning_outcomes: 'Guidance for AI-generated learning outcomes content',
    career_outcomes: 'Guidance for AI-generated career outcomes content',
    program_modules: 'Guidance for AI-generated program modules content',
    program_highlights: 'Guidance for AI-generated program highlights content',
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading instruction hints...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Generation Instruction Hints</h1>
            <p className="text-gray-600">
              Configure optional instruction hints to guide AI content generation for Program entities.
              These hints influence tone, focus, and presentation but do not override validation rules.
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Instruction hints saved successfully!</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-6">
            <div className="flex">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium mb-1">How Instruction Hints Work</p>
                <p className="text-sm">
                  Instruction hints guide AI content generation but cannot override validation rules (schema, format, taxonomy, word counts).
                  They are advisory only and help influence tone, focus, and presentation style.
                </p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Program AI Instruction Hints</h2>
            
            {Object.entries(fieldLabels).map(([fieldName, label]) => (
              <div key={fieldName} className="mb-6 last:mb-0">
                <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700 mb-2">
                  {label} <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  id={fieldName}
                  value={fieldHints[fieldName]}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                  rows={4}
                  maxLength={300}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent resize-y"
                  placeholder={`Describe what you want the AI to include for ${label.toLowerCase()}. This will guide content generation but will not override rules.`}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    {fieldDescriptions[fieldName]}
                  </p>
                  <p className="text-xs text-gray-400">
                    {fieldHints[fieldName].length}/300 characters
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <button
              onClick={fetchInstructionHints}
              disabled={saving || loading}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1E40AF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-6 py-2.5 bg-[#1E40AF] text-white rounded-lg hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Save Instruction Hints</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AIInstructionHints;
